import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({
  baseURL: API,
  withCredentials: true,
});

client.interceptors.request.use((config) => {
  const token = localStorage.getItem("wxs_token");
  if (token) {
    config.headers = config.headers || {};
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  return config;
});

export default client;

// ---- Endpoints ----
export const auth = {
  me: () => client.get("/auth/me"),
  config: () => client.get("/auth/config"),
  devLogin: (email, name) => client.post("/auth/dev-login", { email, name }),
  google: (credential) => client.post("/auth/google", { credential }),
  logout: () => client.post("/auth/logout"),
};

export const settings = {
  getTelegram: () => client.get("/settings/telegram"),
  putTelegram: (payload) => client.put("/settings/telegram", payload),
  getWeex: () => client.get("/settings/weex"),
  putWeex: (payload) => client.put("/settings/weex", payload),
  weexTest: () => client.post("/weex/test"),
  weexDebugBalance: () => client.post("/weex/debug-balance"),
  weexDebugOrder: (symbol, side, notional) =>
    client.post(`/weex/debug-order?symbol=${encodeURIComponent(symbol)}&side=${side}&notional_usdt=${notional}`),
  getRisk: () => client.get("/settings/risk"),
  putRisk: (payload) => client.put("/settings/risk", payload),
  resetKillSwitch: () => client.post("/settings/risk/reset-kill-switch"),
};

export const telegram = {
  connect: () => client.post("/telegram/connect"),
  verify: (code, password) => client.post("/telegram/verify", { code, password }),
  disconnect: () => client.post("/telegram/disconnect"),
  reset: () => client.post("/telegram/reset"),
  healthCheck: () => client.post("/telegram/health-check"),
  dialogs: () => client.get("/telegram/dialogs"),
  reattach: () => client.post("/telegram/reattach"),
  testAlert: () => client.post("/telegram/test-alert"),
  backfill: (from_date, to_date, dry_run = true, use_ai_fallback = false, chat_id = null, ai_cap = null) =>
    client.post("/telegram/backfill",
      { from_date, to_date, dry_run, use_ai_fallback, chat_id, ai_cap },
      { timeout: use_ai_fallback ? 600000 : 180000 },
    ),
};

export const trading = {
  emergencyStop: () => client.post("/trading/emergency-stop"),
  reparseStuck: (dry = true) => client.post(`/admin/reparse-stuck-signals?dry_run=${dry}`),
};

export const channels = {
  list: () => client.get("/channels"),
  create: (body) => client.post("/channels", body),
  update: (id, body) => client.patch(`/channels/${id}`, body),
  remove: (id) => client.delete(`/channels/${id}`),
  killSwitch: (id, active, reason) => client.post(`/channels/${id}/kill-switch`, { active, reason }),
  duplicate: (id, name) => client.post(`/channels/${id}/duplicate`, { name }),
  virtualLedger: (id) => client.get(`/channels/${id}/virtual-balance-ledger`),
};

export const exchanges = {
  get: (name) => client.get(`/settings/exchange/${name}`),
  put: (name, body) => client.put(`/settings/exchange/${name}`, body),
  test: (name) => client.post(`/exchanges/${name}/test`),
  debugOrder: (name, params) => client.post(`/exchanges/${name}/debug-order`, null, { params }),
  debugBalance: (name) => client.post(`/exchanges/${name}/debug-balance`),
  closeAllPositions: (name) => client.post(`/exchanges/${name}/close-all-positions`),
  setAutoTrading: (name, enabled) =>
    client.post(`/exchanges/${name}/auto-trading`, { enabled }),
};

export const signals = {
  ingest: (text, channel, channel_id) =>
    client.post("/signals/ingest", { text, channel, channel_id }),
  parserTest: (text) => client.post("/parser/test", { text }),
  listRaw: () => client.get("/signals/raw"),
  listParsed: () => client.get("/signals/parsed"),
  listPending: () => client.get("/signals/pending"),
  approve: (id) => client.post(`/signals/${id}/approve`),
  reject: (id) => client.post(`/signals/${id}/reject`),
};

export const trades = {
  list: (status) => client.get("/trades", { params: status ? { status } : {} }),
  detail: (id) => client.get(`/trades/${id}`),
  events: (id) => client.get(`/trades/${id}/events`),
  close: (id, partial_percent) => client.post(`/trades/${id}/close`, { partial_percent }),
  breakeven: (id) => client.post(`/trades/${id}/breakeven`),
  mark: () => client.post("/trades/mark"),
  csvUrl: () => `${API}/trades.csv`,
};

export const analytics = {
  perChannelPnl: () => client.get("/analytics/per_channel_pnl"),
  channelMonthly: (id) => client.get(`/analytics/channel/${id}/monthly`),
  channelDiagnostics: (id) => client.get(`/analytics/channel/${id}/signal_diagnostics`),
};

export const backtest = {
  run: (payload) => client.post("/backtest/run", payload),
  compare: (payload) => client.post("/backtest/compare", payload),
};

export const observability = {
  overview: () => client.get("/overview"),
  events: () => client.get("/events"),
  logs: () => client.get("/logs"),
};
